import { useState } from "react";
import axios from "axios";
import { FileText, UploadSimple, DownloadSimple, CheckCircle, WarningCircle, X, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PDF Tools → GSTR-2A Reconciliation.
 *
 * Upload the GSTR-2A file (Excel or JSON) downloaded from the GST portal.
 * The backend matches each 2A entry against the `purchases` collection
 * (which is populated by the Purchase Bill Extractor) and returns a
 * per-row status: MATCH, MISMATCH, MISSING_IN_2A, MISSING_IN_PURCHASES.
 */
export default function GSTR2AReconciliation() {
  const [file, setFile] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const clear = () => { setFile(null); setPreview(null); setError(""); };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("gstr2a_file", file, file.name);
      if (dateFrom) fd.append("date_from", dateFrom);
      if (dateTo) fd.append("date_to", dateTo);
      const r = await axios.post(`${API}/pdf-tools/gstr2a/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally { setBusy(false); }
  };

  const runExcelDownload = async () => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("gstr2a_file", file, file.name);
      if (dateFrom) fd.append("date_from", dateFrom);
      if (dateTo) fd.append("date_to", dateTo);
      const r = await axios.post(`${API}/pdf-tools/gstr2a/reconcile`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, responseType: "blob", timeout: 180000,
      });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url; a.download = `gstr2a-reco-${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      let msg = e?.message || "Download failed";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") { try { const t = await blob.text(); msg = JSON.parse(t).detail || t; } catch(_) { /* keep default msg */ } }
      setError(msg);
    } finally { setBusy(false); }
  };

  const badge = (s) => {
    const map = {
      PRESENT_IN_BOTH_MATCH: "bg-emerald-100 text-emerald-700",
      PRESENT_IN_BOTH_MISMATCH: "bg-rose-100 text-rose-700",
      MISSING_IN_GSTR2A: "bg-amber-100 text-amber-700",
      MISSING_IN_PURCHASES: "bg-rose-100 text-rose-700",
    };
    const label = {
      PRESENT_IN_BOTH_MATCH: "Match",
      PRESENT_IN_BOTH_MISMATCH: "Mismatch",
      MISSING_IN_GSTR2A: "Missing in 2A",
      MISSING_IN_PURCHASES: "Missing in books",
    };
    return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${map[s] || "bg-slate-100 text-slate-600"}`}>{label[s] || s}</span>;
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="pdf-tools-gstr2a-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs font-bold uppercase tracking-widest">
          <FileText size={14} weight="fill" /> PDF Tools · Reconcile
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">GSTR-2A Reconciliation</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Upload the GSTR-2A file (Excel or JSON) from the GST portal. We match every entry against
          your saved Purchases (auto-imported via Purchase Bill Extractor or entered manually) and
          flag mismatches / gaps by supplier GSTIN + invoice number.
        </p>
      </div>

      <div className="bg-white border-2 border-dashed border-slate-300 rounded-sm p-8 text-center hover:border-slate-500 transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { setFile(f); setPreview(null); } }}
      >
        <UploadSimple size={48} weight="duotone" className="mx-auto text-slate-400" />
        <p className="mt-3 text-sm text-slate-600">
          Drag & drop a <b>.xlsx</b> or <b>.json</b> GSTR-2A file, or
          <label className="ml-1 text-slate-900 font-bold underline cursor-pointer">
            browse
            <input type="file" accept=".xlsx,.json,application/json"
              data-testid="gstr2a-file-input"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setError(""); }} />
          </label>
        </p>
        {file && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-sm text-xs font-mono text-slate-700">
            {file.name}
            <button onClick={clear} className="text-slate-400 hover:text-slate-900"><X size={12} /></button>
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Date From (optional)</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="reco-date-from"
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Date To (optional)</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="reco-date-to"
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" />
        </div>
      </div>

      {file && (
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={runPreview} disabled={busy} data-testid="reco-preview-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border border-slate-300 text-slate-800 text-sm font-bold hover:bg-slate-50 disabled:opacity-60">
            Preview Reconciliation
          </button>
          <button onClick={runExcelDownload} disabled={busy} data-testid="reco-download-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-60">
            <DownloadSimple size={18} weight="bold" /> Download Excel
          </button>
        </div>
      )}

      {busy && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-sm p-4 text-sm text-blue-800 flex items-center gap-3">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span>Matching entries…</span>
        </div>
      )}
      {error && <div className="mt-6 bg-rose-50 border border-rose-200 rounded-sm p-4 text-sm text-rose-800 flex items-center gap-2"><WarningCircle size={18} weight="fill" /><span>{error}</span></div>}

      {preview && (
        <div className="mt-8 space-y-4" data-testid="reco-preview-results">
          {/* Count summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card testid="reco-count-match" tone="emerald" label="Match" count={preview.counts?.PRESENT_IN_BOTH_MATCH || 0} />
            <Card testid="reco-count-mismatch" tone="rose" label="Mismatch" count={preview.counts?.PRESENT_IN_BOTH_MISMATCH || 0} />
            <Card testid="reco-count-missing-2a" tone="amber" label="Missing in 2A" count={preview.counts?.MISSING_IN_GSTR2A || 0} />
            <Card testid="reco-count-missing-pur" tone="rose" label="Missing in Books" count={preview.counts?.MISSING_IN_PURCHASES || 0} />
          </div>
          <div className="text-xs text-slate-500">
            Scanned {preview.purchases_scanned} local purchase{preview.purchases_scanned !== 1 ? "s" : ""} against {preview.gstr2a_entries} GSTR-2A entr{preview.gstr2a_entries !== 1 ? "ies" : "y"}.
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto sticky-table">
            <table className="min-w-full text-xs">
              <thead className="sticky-thead bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                <tr>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">GSTIN</th>
                  <th className="px-2 py-2 text-left">Supplier</th>
                  <th className="px-2 py-2 text-left">Invoice</th>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-right">Books Total</th>
                  <th className="px-2 py-2 text-right">2A Total</th>
                  <th className="px-2 py-2 text-right">Diff</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(preview.rows || []).slice(0, 200).map((r, i) => (
                  <tr key={i} className={
                    r.status === "PRESENT_IN_BOTH_MISMATCH" || r.status === "MISSING_IN_PURCHASES" ? "bg-rose-50/50" :
                    r.status === "MISSING_IN_GSTR2A" ? "bg-amber-50/50" : ""
                  }>
                    <td className="px-2 py-1.5">{badge(r.status)}</td>
                    <td className="px-2 py-1.5 font-mono">{r.supplier_gstin}</td>
                    <td className="px-2 py-1.5">{r.supplier_name}</td>
                    <td className="px-2 py-1.5 font-mono">{r.invoice_no}</td>
                    <td className="px-2 py-1.5">{r.invoice_date}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{Number(r.purchase_total || 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{Number(r.gstr2a_total || 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {(() => {
                        const d = Number(r.purchase_total || 0) - Number(r.gstr2a_total || 0);
                        return <span className={Math.abs(d) > 1 ? "text-rose-600 font-bold" : "text-slate-400"}>{d.toFixed(2)}</span>;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(preview.rows || []).length > 200 && (
            <div className="text-xs text-slate-500 flex items-center gap-2"><Warning size={12} /> Showing first 200 rows — download Excel for the complete report.</div>
          )}
        </div>
      )}
    </div>
  );
}

const Card = ({ testid, tone, label, count }) => {
  const map = { emerald: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-700", amber: "bg-amber-50 text-amber-700" };
  return (
    <div className={`border rounded-sm p-4 ${map[tone] || "bg-slate-50 text-slate-700"}`} data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest">{label}</div>
      <div className="text-3xl font-black mt-1">{count}</div>
    </div>
  );
};
